import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
} from "firebase/auth";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import { auth, onAuthStateChanged } from "./firebase";
import {
  clearPendingGoogleAuth,
  formatAuthError,
  readPendingGoogleAuth,
  shouldUseGoogleRedirectFallback,
  storePendingGoogleAuth,
  syncBackendUser,
} from "../utils/authFlow";
import { getAuthReturnPath } from "../utils/authReturnPath";

export default function Login({ showLogin = true, handleClose }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [firebaseUser, setFirebaseUser] = useState(auth.currentUser);
  const redirectSyncHandledRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const redirectTo = useMemo(
    () => getAuthReturnPath(location.state?.from),
    [location.state]
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setFirebaseUser(currentUser || null);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const pendingGoogleAuth = readPendingGoogleAuth();

    if (!pendingGoogleAuth || !firebaseUser || redirectSyncHandledRef.current) {
      return;
    }

    redirectSyncHandledRef.current = true;
    let cancelled = false;

    const finishRedirectLogin = async () => {
      setLoading(true);
      setError(null);

      try {
        await syncBackendUser(firebaseUser);
        clearPendingGoogleAuth();

        if (cancelled) {
          return;
        }

        if (location.pathname === "/login") {
          navigate(getAuthReturnPath(pendingGoogleAuth.redirectTo, redirectTo), { replace: true });
        } else if (showLogin && handleClose) {
          handleClose();
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            "Google login finished, but we could not set up your account: " +
              formatAuthError(err, "Please try again.")
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    finishRedirectLogin();

    return () => {
      cancelled = true;
    };
  }, [firebaseUser, handleClose, location.pathname, navigate, redirectTo, showLogin]);

  const shell = {
    maxWidth: 560,
    margin: showLogin ? "0 auto" : "min(7vh, 48px) auto 0",
    color: "#f5ead0",
  };

  const card = {
    border: "1.5px solid rgba(255, 213, 116, 0.78)",
    borderRadius: 24,
    padding: "clamp(20px, 3vw, 28px)",
    background:
      "linear-gradient(180deg, rgba(12, 19, 38, 0.9), rgba(7, 12, 26, 0.96))",
    boxShadow:
      "0 0 0 1px rgba(255, 239, 183, 0.12), 0 20px 40px rgba(5, 10, 25, 0.35)",
  };

  const badge = {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 12px",
    borderRadius: 999,
    border: "1px solid rgba(255, 213, 116, 0.72)",
    color: "#ffe8a3",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: "0.08em",
    marginBottom: 14,
  };

  const title = {
    color: "#fff1c9",
    fontWeight: 900,
    marginBottom: 8,
  };

  const subtitle = {
    color: "#eadfbe",
    lineHeight: 1.65,
    marginBottom: 18,
  };

  const fieldLabel = {
    color: "#ffe08b",
    fontWeight: 700,
  };

  const input = {
    borderRadius: 14,
    border: "1.5px solid rgba(255, 213, 116, 0.35)",
    background: "rgba(7, 14, 28, 0.9)",
    color: "#fff8e8",
    padding: "12px 14px",
  };

  const submitButton = {
    width: "100%",
    padding: "12px 14px",
    borderRadius: 14,
    border: "1.5px solid rgba(255, 213, 116, 0.78)",
    background:
      "linear-gradient(180deg, rgba(255, 213, 116, 0.96), rgba(232, 173, 52, 0.96))",
    color: "#241703",
    fontWeight: 900,
    boxShadow: "0 12px 24px rgba(5, 10, 25, 0.24)",
  };

  const footerText = {
    textAlign: "center",
    marginTop: 16,
    marginBottom: 0,
    color: "#d8cda8",
  };

  const footerLink = {
    color: "#ffe08b",
    fontWeight: 800,
    textDecoration: "none",
  };

  const divider = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    margin: "16px 0",
    color: "#d8cda8",
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  };

  const dividerLine = {
    flex: 1,
    height: 1,
    background: "rgba(255, 213, 116, 0.22)",
  };

  const googleButton = {
    width: "100%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: "12px 14px",
    borderRadius: 14,
    border: "1.5px solid rgba(255, 213, 116, 0.46)",
    background: "rgba(255, 255, 255, 0.96)",
    color: "#1c1b1a",
    fontWeight: 800,
    boxShadow: "0 10px 24px rgba(5, 10, 25, 0.18)",
  };

  const googleIcon = {
    width: 18,
    height: 18,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    color: "#4285f4",
    background: "#fff",
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email.trim(),
        password
      );
      clearPendingGoogleAuth();
      await syncBackendUser(userCredential.user, {
        username: email.trim().split("@")[0] || "user",
      });

      if (showLogin && handleClose) {
        handleClose();
      } else {
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      console.error("Login failed:", err);
      setError("Login failed: " + formatAuthError(err, "Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError(null);

    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });

    try {
      const result = await signInWithPopup(auth, provider);
      clearPendingGoogleAuth();

      try {
        await syncBackendUser(result.user);
      } catch (syncError) {
        console.warn("Google login succeeded but backend sync failed:", syncError);
      }

      if (showLogin && handleClose) {
        handleClose();
      } else {
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      if (shouldUseGoogleRedirectFallback(err)) {
        storePendingGoogleAuth(redirectTo);
        await signInWithRedirect(auth, provider);
        return;
      }

      console.error("Google login failed:", err);
      clearPendingGoogleAuth();
      setError(
        "Google login failed: " + formatAuthError(err, "Please try again.")
      );
    } finally {
      setLoading(false);
    }
  };

  const formContent = (
    <div style={shell}>
      <div style={card}>
        <div style={badge}>TRAINING LOGIN</div>
        <h2 style={title}>Log in to start training</h2>
        <p style={subtitle}>
          Sign in to unlock your saved puzzles, rating progress, hints,
          successes and failures. After login you will go straight to training.
        </p>

        <Form onSubmit={handleLogin}>
          <Form.Group className="mb-3" controlId="formBasicEmail">
            <Form.Label style={fieldLabel}>Email address</Form.Label>
            <Form.Control
              type="email"
              placeholder="Enter email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={input}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="formBasicPassword">
            <Form.Label style={fieldLabel}>Password</Form.Label>
            <Form.Control
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={input}
            />
          </Form.Group>

          {error && <Alert variant="danger">{error}</Alert>}

          <Button type="submit" disabled={loading} style={submitButton}>
            {loading ? "Logging in..." : "Log In"}
          </Button>
        </Form>

        <div style={divider}>
          <span style={dividerLine} />
          <span>or</span>
          <span style={dividerLine} />
        </div>

        <button type="button" onClick={handleGoogleLogin} disabled={loading} style={googleButton}>
          <span style={googleIcon}>G</span>
          {loading ? "Please wait..." : "Continue with Google"}
        </button>

        {!showLogin && (
          <p style={footerText}>
            No account yet?{" "}
            <Link to="/register" state={{ from: location.state?.from }} style={footerLink}>
              Create one here
            </Link>
          </p>
        )}
      </div>
    </div>
  );

  if (showLogin) {
    return (
      <Modal show onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Login</Modal.Title>
        </Modal.Header>
        <Modal.Body>{formContent}</Modal.Body>
      </Modal>
    );
  }

  return formContent;
}

